// Hook pour charger les chantiers avec stats d'engagement et filtrage dynamique
import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useChantiersRealtime } from './useChantiersRealtime';
import { FiltreRentabilite, FILTRES_CONFIG } from '@/constants/filtresRentabilite';
import { format } from 'date-fns';
import { getDateFinEffective, AffectationRetard, SuspensionInfo as SuspensionInfoDateUtils } from '@/lib/dateUtils';
import { BENCHMARKS_BTP } from '@/lib/calculs-rentabilite';

export interface ChantierRentabilite {
  id: string;
  nom_chantier: string;
  client: string;
  adresse: string;
  etat_chantier: string;
  statut: string;
  budget_ht: number;
  date_debut_prevue?: string;
  date_fin_estimee?: string;
  date_fin_initiale?: string; // Date de fin AVANT ajout de retard
  date_fin_effective?: string; // Date de fin incluant retards et suspensions
  dateFinRetard?: string; // Date de fin max des affectations retard (pour affichage)
  jours_retard?: number; // Nombre de jours de retard déclarés
  pictogramme?: string;
  couleur?: string;
  // Compteurs pour déterminer l'engagement
  factures_count: number;
  affectations_count: number;
  // Métriques calculées
  metrics: {
    couts_fixes_engages: number;
    cout_main_oeuvre_reel: number;
    marge_finale: number;
    marge_finale_pct: number;
  } | null;
  // Calculé
  est_engage: boolean;
}

export interface KPIs {
  nombreChantiers: number;
  totalRevenus: number;
  totalCouts: number;
  margeGlobale: number;
  rentabiliteMoyenne: number;
  chantiersEnPerte: number;
  chantiersAuDessusObjectif: number;
}

// Hook principal pour charger tous les chantiers avec stats d'engagement
export function useChantiersFiltresRentabilite(entrepriseId: string | null) {
  const [chantiers, setChantiers] = useState<ChantierRentabilite[]>([]);
  const [loading, setLoading] = useState(true);

  const loadChantiers = useCallback(async () => {
    if (!entrepriseId) return;

    setLoading(true);
    try {
      // 1. Charger les chantiers
      const { data, error } = await supabase
        .from('chantiers')
        .select('*')
        .eq('entreprise_id', entrepriseId)
        .order('date_creation', { ascending: false });

      if (error) throw error;

      // 2. Charger métriques pour chaque chantier individuellement
      const chantiersAvecStats = await Promise.all(
        (data || []).map(async (c: any) => {
          // Charger métriques
          const { data: metricsData } = await supabase
            .from('chantier_metrics_realtime')
            .select('metrics')
            .eq('chantier_id', c.id)
            .single();

          const metrics = metricsData?.metrics as any || null;

          // Compter factures
          const { count: facturesCount } = await supabase
            .from('factures_fournisseurs')
            .select('*', { count: 'exact', head: true })
            .eq('chantier_id', c.id);

          // Compter affectations
          const { count: affectationsCount } = await supabase
            .from('affectations_chantiers')
            .select('*', { count: 'exact', head: true })
            .eq('chantier_id', c.id);

          // Charger les affectations de retard pour calculer la date effective
          const { data: retardAffectations } = await supabase
            .from('affectations_chantiers')
            .select('date_fin, jours_travailles, jours_details')
            .eq('chantier_id', c.id)
            .like('notes', 'Retard chantier%');

          // Préparer les données retard pour getDateFinEffective
          const affectationsRetardData: AffectationRetard[] = (retardAffectations || []).map(a => ({
            date_fin: a.date_fin,
            jours_travailles: a.jours_travailles || 0,
            jours_details: a.jours_details as AffectationRetard['jours_details']
          }));

          // Charger les suspensions terminées
          const { data: suspensionsTerminees } = await supabase
            .from('suspensions_chantiers')
            .select('date_suspension, date_reprise, duree_suspension_jours')
            .eq('chantier_id', c.id)
            .not('date_reprise', 'is', null);

          const suspensionsForCalc: SuspensionInfoDateUtils[] = (suspensionsTerminees || []).map(s => ({
            date_suspension: s.date_suspension,
            date_reprise: s.date_reprise,
            duree_suspension_jours: s.duree_suspension_jours
          }));

          // Calculer la date de fin effective
          const dateFinEffectiveDate = getDateFinEffective({
            date_fin_estimee: c.date_fin_estimee,
            date_fin_initiale: c.date_fin_initiale,
            affectationsRetard: affectationsRetardData,
            suspensionsTerminees: suspensionsForCalc
          });
          const dateFinEffective = dateFinEffectiveDate
            ? format(dateFinEffectiveDate, 'yyyy-MM-dd')
            : c.date_fin_estimee;

          // Calculer le nombre total de jours de retard
          // IMPORTANT: Ne pas sommer jours_travailles car c'est par membre d'équipe
          // Ex: 1 jour de retard avec 3 membres = 3 affectations avec jours_travailles=1
          // On doit compter les jours UNIQUES via jours_details
          let joursRetard = 0;
          if (affectationsRetardData.length > 0) {
            // Collecter tous les jours_details de toutes les affectations
            const uniqueDelayDays = new Set<string>();
            for (const aff of affectationsRetardData) {
              if (aff.jours_details && Array.isArray(aff.jours_details)) {
                for (const detail of aff.jours_details) {
                  // Clé unique: date:period (ex: "2026-02-06:AM")
                  uniqueDelayDays.add(`${detail.date}:${detail.period}`);
                }
              }
            }

            if (uniqueDelayDays.size > 0) {
              // Compter: FULL = 1 jour, AM ou PM = 0.5 jour
              for (const key of uniqueDelayDays) {
                const period = key.split(':')[1];
                joursRetard += period === 'FULL' ? 1 : 0.5;
              }
            } else {
              // Fallback si pas de jours_details: prendre le max jours_travailles
              // (tous les membres du même retard ont le même jours_travailles)
              joursRetard = Math.max(...affectationsRetardData.map(a => a.jours_travailles || 0), 0);
            }
          }

          // Calculer dateFinRetard = max date_fin des affectations retard (pour affichage)
          // C'est la vraie date de fin effective du retard (comme sur ProjectCard)
          const dateFinRetard = affectationsRetardData.length > 0
            ? affectationsRetardData.reduce((max, a) => a.date_fin > max ? a.date_fin : max, affectationsRetardData[0].date_fin)
            : undefined;

          // Logique d'engagement :
          // - en_cours, suspendu, termine = toujours engagé
          // - planifie ou annule = engagé seulement si factures OU affectations
          const statutsEngages = ['en_cours', 'suspendu', 'termine'];
          const estEngageParStatut = statutsEngages.includes(c.etat_chantier);
          const estEngageParTransaction =
            ['planifie', 'annule'].includes(c.etat_chantier) &&
            ((facturesCount || 0) > 0 || (affectationsCount || 0) > 0);

          return {
            id: c.id,
            nom_chantier: c.nom_chantier,
            client: c.client,
            adresse: c.adresse || '',
            etat_chantier: c.etat_chantier,
            statut: c.statut,
            budget_ht: metrics?.budget_ht || c.budget_ht || 0,
            date_debut_prevue: c.date_debut_prevue,
            date_fin_estimee: c.date_fin_estimee,
            date_fin_initiale: c.date_fin_initiale, // Date avant retard
            date_fin_effective: dateFinEffective,
            dateFinRetard, // Date fin max des affectations retard (pour affichage)
            jours_retard: joursRetard,
            pictogramme: c.pictogramme,
            couleur: c.couleur,
            factures_count: facturesCount || 0,
            affectations_count: affectationsCount || 0,
            metrics,
            est_engage: (estEngageParStatut || estEngageParTransaction) && c.etat_chantier !== 'archive',
          } as ChantierRentabilite;
        })
      );

      setChantiers(chantiersAvecStats);
    } catch (error) {
      console.error('Erreur chargement chantiers:', error);
    } finally {
      setLoading(false);
    }
  }, [entrepriseId]);

  useEffect(() => {
    loadChantiers();
  }, [loadChantiers]);

  // Abonnement realtime
  useChantiersRealtime(entrepriseId, loadChantiers);

  return { chantiers, loading, refresh: loadChantiers };
}

// Hook pour filtrer les chantiers et calculer les KPIs dynamiquement
export function useChantiersFiltres(
  chantiers: ChantierRentabilite[],
  filtre: FiltreRentabilite
) {
  return useMemo(() => {
    let filtered = chantiers;
    const config = FILTRES_CONFIG[filtre];

    switch (filtre) {
      case 'engage':
        filtered = chantiers.filter(c => c.est_engage);
        break;
      case 'tous':
        // "Tous" = vraiment tous les chantiers (y compris archivés)
        filtered = chantiers;
        break;
      case 'archive':
        filtered = chantiers.filter(c => c.etat_chantier === 'archive');
        break;
      case 'projections':
        filtered = chantiers.filter(c =>
          ['brouillon', 'devis_envoye'].includes(c.etat_chantier) &&
          c.etat_chantier !== 'archive'
        );
        break;
      default:
        if (config.statuts) {
          filtered = chantiers.filter(c =>
            config.statuts!.includes(c.etat_chantier) &&
            c.etat_chantier !== 'archive'
          );
        }
    }

    // Calcul des KPIs sur les chantiers filtrés
    const kpis: KPIs = {
      nombreChantiers: filtered.length,
      totalRevenus: filtered.reduce((sum, c) => sum + (c.budget_ht || 0), 0),
      totalCouts: filtered.reduce((sum, c) => {
        const m = c.metrics;
        return sum + (m ? (m.couts_fixes_engages + m.cout_main_oeuvre_reel) : 0);
      }, 0),
      margeGlobale: filtered.reduce((sum, c) => sum + (c.metrics?.marge_finale || 0), 0),
      rentabiliteMoyenne: filtered.length > 0
        ? filtered.reduce((sum, c) => sum + (c.metrics?.marge_finale_pct || 0), 0) / filtered.length
        : 0,
      chantiersEnPerte: filtered.filter(c => (c.metrics?.marge_finale || 0) < 0).length,
      chantiersAuDessusObjectif: filtered.filter(c =>
        (c.metrics?.marge_finale_pct ?? 0) >= BENCHMARKS_BTP.marge_min
      ).length,
    };

    return { filtered, kpis };
  }, [chantiers, filtre]);
}

// Hook pour calculer les compteurs de chaque filtre (pour badges)
export function useCompteursFiltres(chantiers: ChantierRentabilite[]) {
  return useMemo(() => {
    return {
      engage: chantiers.filter(c => c.est_engage).length,
      // "Tous" = vraiment tous les chantiers (y compris archivés)
      tous: chantiers.length,
      en_cours: chantiers.filter(c => c.etat_chantier === 'en_cours' && c.etat_chantier !== 'archive').length,
      termine: chantiers.filter(c => c.etat_chantier === 'termine' && c.etat_chantier !== 'archive').length,
      suspendu: chantiers.filter(c => c.etat_chantier === 'suspendu' && c.etat_chantier !== 'archive').length,
      projections: chantiers.filter(c =>
        ['brouillon', 'devis_envoye'].includes(c.etat_chantier) &&
        c.etat_chantier !== 'archive'
      ).length,
      archive: chantiers.filter(c => c.etat_chantier === 'archive').length,
    };
  }, [chantiers]);
}
